import { BodyPrimary } from '@liveauctioneers/hammer-ui-core/text';
import { CatalogNearMe } from '@/redux/modules/auctionsNearMe/catalogsNearMe.types';
import { fetchCatalogsIfNeeded } from '@/redux/modules/catalog/catalogs/catalog.actions';
import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import { GeoArea } from '@/types/GeoArea';
import { Loading } from '@liveauctioneers/hammer-ui-core/loading';
import { RelatedGeoAreasNew } from '@/components/RelatedGeoAreas/RelatedGeoAreasNew';
import { useAppDispatch } from '@/redux/hooks';
import { useEffect, useMemo } from 'react';
import AuctionsNearMeCatalogGrid from './AuctionsNearMeCatalogGrid';
import AuctionsNearMeFilterBar from './components/FilterBar/AuctionsNearMeFilterBar';
import Pagination from '@/components/Pagination/hammer-pagination/Pagination';

export type Props = {
    catalogsNearMe: CatalogNearMe[];
    geoArea?: GeoArea;
    handleKeywordsChange: (keywords: string[]) => void;
    handlePageChange: (page: number) => void;
    handlePageSizeChange: (pageSize: number) => void;
    loading: boolean;
    pageNumber: number;
    pageSize: number;
    toggleLocalPickupFilter: () => void;
    toggleTopRatedFilter: () => void;
    totalRecords: number;
};

export const AuctionsNearMeCardsViewNew = ({
    catalogsNearMe,
    geoArea,
    handleKeywordsChange,
    handlePageChange,
    handlePageSizeChange,
    loading,
    pageNumber,
    pageSize,
    toggleLocalPickupFilter,
    toggleTopRatedFilter,
    totalRecords,
}: Props) => {
    const dispatch = useAppDispatch();

    const catalogIds = useMemo(() => catalogsNearMe.map((catalog) => catalog.id), [catalogsNearMe]);

    // Fetch full catalog details for the current page of results
    useEffect(() => {
        if (catalogIds.length > 0) {
            dispatch(fetchCatalogsIfNeeded({ catalogIds }));
        }
    }, [catalogIds, dispatch]);

    const handlePaginationChange = ({ page, pageSize: newPageSize }: { page: number; pageSize?: number }) => {
        if (newPageSize && newPageSize !== pageSize) {
            handlePageSizeChange(newPageSize);
        } else if (page !== pageNumber) {
            handlePageChange(page);
        }
    };

    const catalogsView = (
        <section>
            <Pagination
                className="!grid-cols-[1fr_auto] [&>:first-child]:hidden"
                onChange={handlePaginationChange}
                pageSizeOptions={[24, 48, 72, 96, 120]}
                paginationFilter={{
                    page: pageNumber,
                    pageSize,
                    totalRecords,
                }}
            >
                <AuctionsNearMeCatalogGrid catalogIds={catalogIds} />
            </Pagination>
        </section>
    );

    const noCatalogsView = (
        <section>
            <BodyPrimary>
                <FormattedMessage id="no_auctions_available_in_area" />
            </BodyPrimary>
        </section>
    );

    // Only show the full loading spinner when there's no data at all (initial load).
    // When data already exists, keep showing it while the new fetch is in flight.
    const showInitialLoading = loading && catalogIds.length === 0 && totalRecords === 0;

    const results = catalogIds.length > 0 ? catalogsView : noCatalogsView;

    if (showInitialLoading) {
        return <Loading />;
    }

    return (
        <div>
            {/* Sticky Header */}
            <div className="smMin:sticky smMin:top-0 smMin:z-[11] smMin:bg-background-primary smMin:pb-sm smMin:pt-lg">
                <AuctionsNearMeFilterBar
                    numberOfAuctions={totalRecords}
                    onKeywordsChange={handleKeywordsChange}
                    toggleLocalPickUpFilter={toggleLocalPickupFilter}
                    toggleTopRatedFilter={toggleTopRatedFilter}
                />
            </div>
            {/* Cards */}
            {results}
            {/* "More Locations" */}
            <div className="mt-2xl">
                <RelatedGeoAreasNew geoArea={geoArea} />
            </div>
        </div>
    );
};
